import { useState } from 'react';
import { useBill } from '../bill.tsx';
import { useStore } from '../store.tsx';
import { Field, Modal } from '../ui.tsx';
import type { BillType } from '../lib/types.ts';

/**
 * เลือกประเภทออเดอร์
 *
 * ★ ลอกจาก components/NewBillModal.js ★
 *   · การ์ดรูป 130×77.8 เรียงซ้าย — ทานที่ร้าน / กลับบ้าน / เจ้าเดลิเวอรี่ทีละเจ้า
 *   · การ์ดที่เลือกอยู่ขอบหนา 5px สีหลัก
 *   · ขวาเป็นรายละเอียดตามประเภทที่เลือก
 *       ทานที่ร้าน  จำนวนคน มีปุ่ม − / + เลขใหญ่ 40px
 *       กลับบ้าน    ชื่อ เบอร์โทร หมายเหตุ
 *       เดลิเวอรี่    เลขออเดอร์ของเจ้านั้น
 *   · ปุ่มล่าง: ปิด / ยืนยัน
 *
 * ★ ยืนยันแล้วยังไม่สร้างบิลในระบบ ★ เหมือนของเดิม — บิลเกิดตอนกด "บันทึก"
 * บิลเปล่าที่ค้างในระบบคือขยะที่ไม่มีใครกล้าลบ
 */
export function NewBillModal({
  onClose,
  onConfirmed,
}: {
  onClose: () => void;
  onConfirmed: (billType: BillType) => void;
}) {
  const store = useStore();
  const bill = useBill();

  type Choice =
    | { type: 'dine_in' }
    | { type: 'takeaway' }
    | { type: 'delivery'; channelId: number; name: string };

  const choices: Choice[] = [
    { type: 'dine_in' },
    { type: 'takeaway' },
    ...store.deliveryChannels.map((c) => ({
      type: 'delivery' as const, channelId: c.id, name: c.name,
    })),
  ];

  const [choice, setChoice] = useState<Choice>(choices[0]!);
  const [guests, setGuests] = useState(1);
  const [orderRef, setOrderRef] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');

  const label = (c: Choice) =>
    c.type === 'dine_in' ? 'ทานที่ร้าน' : c.type === 'takeaway' ? 'สั่งกลับบ้าน' : c.name;

  const image = (c: Choice) => {
    if (c.type === 'dine_in') return '/ui/restaurant-order.png';
    if (c.type === 'takeaway') return '/ui/online-order.png';
    if (/grab/i.test(c.name)) return '/ui/delivery/grab.png';
    if (/panda/i.test(c.name)) return '/ui/delivery/food-panda.png';
    if (/line/i.test(c.name)) return '/ui/delivery/lineman.png';
    return '/ui/online-order.png';
  };

  const same = (a: Choice, b: Choice) =>
    a.type === b.type &&
    (a.type !== 'delivery' || b.type !== 'delivery' || a.channelId === b.channelId);

  const confirm = () => {
    bill.reset();
    bill.setPayload({
      billType: choice.type,
      guestCount: choice.type === 'dine_in' ? guests : 0,
      deliveryChannelId: choice.type === 'delivery' ? choice.channelId : null,
      deliveryName: choice.type === 'delivery' ? choice.name : '',
      deliveryOrderRef: choice.type === 'delivery' ? orderRef.trim() : phone.trim(),
      customerName: choice.type === 'takeaway' ? name.trim() : '',
      customerPhone: choice.type === 'takeaway' ? phone.trim() : '',
    });
    onConfirmed(choice.type);
  };

  return (
    <Modal title={`ประเภทออเดอร์ ${label(choice)}`} onClose={onClose} wide>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <div className="type-grid" style={{ flex: 1, minWidth: 290 }}>
          {choices.map((c, i) => (
            <button
              key={i}
              className="type-card"
              aria-pressed={same(c, choice)}
              onClick={() => setChoice(c)}
            >
              <img src={image(c)} alt="" />
              <span>{label(c)}</span>
            </button>
          ))}
        </div>

        <div style={{ flex: 1, minWidth: 260 }}>
          {choice.type === 'dine_in' && (
            <Field label="จำนวนลูกค้า">
              <div className="qty-row" style={{ justifyContent: 'center', gap: 20 }}>
                <button className="qty-btn" onClick={() => setGuests((n) => Math.max(1, n - 1))}>−</button>
                <span style={{ fontSize: 40, fontWeight: 700, minWidth: 70, textAlign: 'center' }}>
                  {guests}
                </span>
                <button className="qty-btn" onClick={() => setGuests((n) => n + 1)}>+</button>
              </div>
              <div style={{ color: 'var(--ink-3)', fontSize: '.85rem', marginTop: 8, textAlign: 'center' }}>
                ใช้คิดยอดเฉลี่ยต่อคนในรายงาน
              </div>
            </Field>
          )}

          {choice.type === 'takeaway' && (
            <>
              <Field label="ชื่อลูกค้า">
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="ไม่บังคับ" />
              </Field>
              <Field label="เบอร์โทร">
                <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="ไม่บังคับ" />
              </Field>
            </>
          )}

          {choice.type === 'delivery' && (
            <Field label={`เลขออเดอร์ของ ${choice.name}`}>
              <input
                className="money"
                inputMode="numeric"
                value={orderRef}
                onChange={(e) => setOrderRef(e.target.value)}
                placeholder="ไม่บังคับ"
                autoFocus
              />
              <div className="numpad" style={{ marginTop: 10 }}>
                {['7', '8', '9', '4', '5', '6', '1', '2', '3'].map((k) => (
                  <button key={k} onClick={() => setOrderRef((p) => p + k)}>{k}</button>
                ))}
                <button onClick={() => setOrderRef('')} className="erase">ล้าง</button>
                <button onClick={() => setOrderRef((p) => p + '0')}>0</button>
                <button onClick={() => setOrderRef((p) => p.slice(0, -1))} className="erase">ลบ</button>
              </div>
            </Field>
          )}
        </div>
      </div>

      <div className="action-row" style={{ marginTop: 16 }}>
        <button className="btn ghost" onClick={onClose}>ปิด</button>
        <button className="btn" onClick={confirm}>ยืนยัน</button>
      </div>
    </Modal>
  );
}
